import { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  Video,
  Coins,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  Users,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { useOnboarding } from "@/hooks/useOnboarding";
import { Skeleton } from "@/components/ui/skeleton";
import { ZeroCreditsDialog } from "@/components/dashboard/ZeroCreditsDialog";
import { OnboardingFlow } from "@/components/onboarding";
import { MobileNav } from "@/components/mobile/MobileNav";

interface DashboardLayoutProps {
  children: ReactNode;
}

const navItems = [
  { href: "/dashboard", label: "Minhas Publis", icon: LayoutDashboard },
  { href: "/dashboard/nova-task", label: "Gerar Nova Publi", icon: Video },
  { href: "/dashboard/tasks", label: "Missões", icon: Target },
  { href: "/dashboard/creditos", label: "Créditos", icon: Coins },
  { href: "/dashboard/configuracoes", label: "Configurações", icon: Settings },
];

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { availableCredits, isLoading: creditsLoading } = useCredits();
  const { shouldShow: showOnboarding, markAsCompleted: completeOnboarding } = useOnboarding();
  const [showZeroCreditsDialog, setShowZeroCreditsDialog] = useState(false);

  useEffect(() => {
    const isRelevantPage = location.pathname === '/dashboard' || location.pathname === '/dashboard/nova-task';
    const alreadyShown = sessionStorage.getItem('zero_credits_dialog_shown');
    if (!creditsLoading && availableCredits === 0 && isRelevantPage && !alreadyShown) {
      setShowZeroCreditsDialog(true);
      sessionStorage.setItem('zero_credits_dialog_shown', 'true');
    }
  }, [availableCredits, creditsLoading, location.pathname]);

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  const userName = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "Usuário";

  return (
    <div className="min-h-screen bg-background flex">
      {/* Onboarding Flow - shows only once per user */}
      {showOnboarding && (
        <OnboardingFlow onComplete={completeOnboarding} />
      )}

      <ZeroCreditsDialog
        open={showZeroCreditsDialog && !showOnboarding}
        onOpenChange={setShowZeroCreditsDialog}
      />
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card/50">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl font-bold text-foreground tracking-tight">
              Pub<span className="text-gradient">lee</span>
            </span>
          </Link>
        </div>

        <nav className="flex-1 px-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-all w-full"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Sair</span>
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border z-50 lg:hidden"
            >
              <div className="p-6 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2">
                  <span className="text-2xl font-bold tracking-tight">
                    Pub<span className="text-gradient">lee</span>
                  </span>
                </Link>
                <button onClick={() => setSidebarOpen(false)}>
                  <X className="w-6 h-6" />
                </button>
              </div>

              <nav className="px-4 space-y-1">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                        }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>

              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-all w-full"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Sair</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="h-16 border-b border-border bg-card/50 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 hover:bg-secondary rounded-lg"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            <span className="text-foreground font-medium">
              E aí, <span className="text-gradient">{userName}</span> 👋
            </span>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-secondary rounded-lg">
              <Coins className="w-4 h-4 text-primary" />
              {creditsLoading ? (
                <Skeleton className="h-5 w-6" />
              ) : (
                <span className="font-semibold">{availableCredits}</span>
              )}
              <span className="text-muted-foreground text-sm">créditos</span>
            </div>
            <Button variant="outline" size="sm" className="hidden sm:flex gap-2" asChild>
              <Link to="/dashboard/creditos">
                <Plus className="w-4 h-4" />
                Adicionar
              </Link>
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto pb-24 lg:pb-8">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden">
        <MobileNav />
      </div>
    </div>
  );
}
